import type { QueryClient } from '@tanstack/react-query';
import {
  getTradingOrderGetCountsQueryKey,
  getTradingOrderListOrdersQueryKey,
  getTradingPositionGetCountsQueryKey,
  getTradingPositionGetPnlQueryKey,
  getTradingPositionGetPortfolioQueryKey,
  getTradingPositionListPositionsQueryKey,
  getTradingTradeListTradesQueryKey,
} from '@/lib/endpoints';

/**
 * Invalidate the trading mirrors (positions + orders + trades) and the
 * portfolio summary so React Query refetches them. Single source for the
 * query-key set — used by mutation success handlers, the realtime sync, and
 * settlement resync alike. The portfolio summary feeds the Portfolio page,
 * SideNav and Wallet cards, so it must move with the positions it aggregates.
 */
export function invalidateTradingCaches(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: getTradingPositionListPositionsQueryKey() });
  void queryClient.invalidateQueries({ queryKey: getTradingOrderListOrdersQueryKey() });
  void queryClient.invalidateQueries({ queryKey: getTradingTradeListTradesQueryKey() });
  void queryClient.invalidateQueries({ queryKey: getTradingPositionGetPortfolioQueryKey() });
  // Tab badges move with the lists they count.
  void queryClient.invalidateQueries({ queryKey: getTradingPositionGetCountsQueryKey() });
  void queryClient.invalidateQueries({ queryKey: getTradingOrderGetCountsQueryKey() });
  // Realized-PnL series (portfolio chart) moves on sells and settlements.
  void queryClient.invalidateQueries({ queryKey: getTradingPositionGetPnlQueryKey() });
}

/** Trailing-debounce window for the realtime/settlement paths (ms). */
const DEBOUNCE_MS = 200;
let debounceTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Debounced {@link invalidateTradingCaches} — coalesces a burst into ONE refetch
 * cycle: a single buy emits `order:filled` + `trade:executed` (+ partial fills),
 * and several markets can settle together. The module-level timer is shared on
 * purpose — the app has one QueryClient and every caller wants the same
 * account-wide invalidation, so collapsing them is always correct. Use the
 * immediate variant for the user's own mutation success (instant feedback).
 */
export function invalidateTradingCachesDebounced(queryClient: QueryClient): void {
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    debounceTimer = null;
    invalidateTradingCaches(queryClient);
  }, DEBOUNCE_MS);
}
